import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import CountdownTimer from 'adminDashboard/home/common/countdownTimer'
import { Col, Row, Skeleton } from 'antd'
import moment from 'moment'

import { getRenewalPlanPeriod } from 'core'
import { User } from 'core/application/account/loginUser'
import { PlanPeriodDto } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import InitiateQsehraRenewal from 'core/application/benefit/benefitRenewal/qsehra/InitiateQsehraBenefitRenewal'
import isOpenEnrollmentPeriod from 'core/application/benefit/benefitRenewal/qsehra/IsOpenEnrollmentPeriod'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'

import Avatar from '../../../../common/components/dataDisplay/Avatar'
import Card from '../../../../common/components/dataDisplay/Card'
import Button from '../../../../common/components/general/Button'
import { parseZone, parseZoneFormat } from '../../../../common/dateFormatters'
import { useCommandRequest } from '../../../../common/hooks'
import useQueryRequest from '../../../../common/hooks/useQueryRequest'
import { ROUTE_PATHS } from '../../../../common/routePaths'

import './styles.less'

type AdminCountdownTimerProps = {
  planPeriod: PlanPeriodDto
  user: User
}

const daysLeftForOpenEnrollment = () => {
  const startDate = moment()
  startDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
  const endDate = moment('01/11/' + startDate.year(), 'DD/MM/YYYY')
  return endDate.diff(startDate, 'days')
}

function AdminCountdownTimer({ ...props }: AdminCountdownTimerProps) {
  const history = useHistory()

  const { t: translate } = useTranslation()
  const [renewalPlanPeriod, isRenewalPlanPeriodLoading] = useQueryRequest(undefined, () => getRenewalPlanPeriod())
  const [sendCommand, isCommandLoading] = useCommandRequest()

  const onInitiateRenewal = async () => {
    const result = await sendCommand(() => InitiateQsehraRenewal())
    if (result.completed) {
      history.push(ROUTE_PATHS.RENEWAL_TASKS)
    }
  }

  const nextYear = moment().add(1, 'years').year()

  const days = daysLeftForOpenEnrollment()

  return (
    <Skeleton loading={isRenewalPlanPeriodLoading}>
      {(renewalPlanPeriod === null ||
        (renewalPlanPeriod != null && renewalPlanPeriod.status !== BenefitDesignStatus.Signed)) && (
        <Card style={{ backgroundColor: '#E6FFFF', border: '1px solid #8DF3FC' }}>
          <Row gutter={16}>
            <Col>
              <CountdownTimer days={days} />
            </Col>

            <Col xxl={18} xl={16} lg={13} md={13} sm={13} xs={24}>
              <h4>
                {renewalPlanPeriod?.notificationDate
                  ? `Your benefit is ready for ${parseZone(renewalPlanPeriod.startDate).year()}`
                  : 'Renew your benefit plan now'}
              </h4>
              {renewalPlanPeriod?.notificationDate ? (
                <p>
                  Hi {props.user?.firstName}, you’re all set for {parseZone(renewalPlanPeriod.startDate).year()}! We’ll
                  automatically launch your benefit on December 31 with your plan elections and notify your employees.
                  However, if you want to make changes, you still can until December 31.
                </p>
              ) : (
                <p>
                  Hi {props.user?.firstName}, if you want to change your benefit elections for {nextYear}, like
                  increasing your allowance amounts, our simple tool will walk you through the process.
                </p>
              )}

              <Row gutter={16} className="mb-24 mt-24">
                <Col>
                  {renewalPlanPeriod === null && (
                    <Button
                      type="primary"
                      loading={isCommandLoading}
                      disabled={isCommandLoading}
                      onClick={onInitiateRenewal}
                    >
                      Renew benefit plan
                    </Button>
                  )}

                  {renewalPlanPeriod !== null && !renewalPlanPeriod.notificationDate && (
                    <Button
                      type="primary"
                      onClick={() => {
                        history.push(ROUTE_PATHS.RENEWAL_TASKS)
                      }}
                    >
                      In Progress - Continue where you left off
                    </Button>
                  )}

                  {renewalPlanPeriod !== null && renewalPlanPeriod.notificationDate && (
                    <Button
                      type="primary"
                      onClick={() => {
                        history.push(`${ROUTE_PATHS.BENEFIT_DETAILS_PATH}?planPeriodId=${renewalPlanPeriod.id}`)
                      }}
                    >
                      Make changes to the {parseZone(renewalPlanPeriod.startDate).year()} benefit
                    </Button>
                  )}
                </Col>
                <Col className="mt-4">
                  <a target={'_blank'} href="https://www.peoplekeep.com/state-open-enrollment">
                    <u>{translate('admin-dashboard.home.countdown-notification.link')}</u>
                  </a>
                </Col>
              </Row>
              <p>
                {renewalPlanPeriod?.notificationDate ? (
                  <b>
                    View your {parseZone(renewalPlanPeriod?.startDate).year()} benefit settings by going to Benefit
                    Details.
                  </b>
                ) : (
                  <b>
                    If you take no action, we will automatically renew your benefit with its existing elections on
                    December 1 for a January 1, {nextYear}, benefit start. You can still make changes to your benefit
                    until December 31 by going to Benefit Details.
                  </b>
                )}
              </p>
            </Col>
          </Row>
        </Card>
      )}
    </Skeleton>
  )
}

export default AdminCountdownTimer
